export const PROD_GRAPHQL_ENDPOINT =
  "/api/graphql";
export const LOCAL_GRAPHQL_ENDPOINT = "/graphql";
export const GRAPHQL_ENDPOINT_QUERY_PARAM = "graphqlEndpoint";
export const GRAPHQL_ENDPOINT_STORAGE_KEY = "ws_graphql_endpoint_override_v1";

const RESET_OVERRIDE_VALUES = new Set(["reset", "default", "clear"]);
const LOCAL_OVERRIDE_VALUES = new Set(["local", "localhost"]);
const PROD_OVERRIDE_VALUES = new Set(["prod", "production", "live"]);
const LEGACY_LOCAL_ENDPOINT_VALUES = new Set([
  "http://127.0.0.1:4999/graphql",
  "http://localhost:4999/graphql",
]);

function safeReadStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function safeWriteStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Geen localStorage beschikbaar
  }
}

function safeRemoveStorage(key) {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // Geen localStorage beschikbaar
  }
}

function parseOverrideValue(value) {
  if (typeof value !== "string") {
    return { action: "none", endpoint: "" };
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return { action: "none", endpoint: "" };
  }

  const normalized = trimmed.toLowerCase();
  if (RESET_OVERRIDE_VALUES.has(normalized)) {
    return { action: "reset", endpoint: "" };
  }

  if (LOCAL_OVERRIDE_VALUES.has(normalized)) {
    return { action: "set", endpoint: LOCAL_GRAPHQL_ENDPOINT };
  }

  if (PROD_OVERRIDE_VALUES.has(normalized)) {
    return { action: "set", endpoint: PROD_GRAPHQL_ENDPOINT };
  }

  if (LEGACY_LOCAL_ENDPOINT_VALUES.has(normalized)) {
    return { action: "set", endpoint: LOCAL_GRAPHQL_ENDPOINT };
  }

  return { action: "set", endpoint: trimmed };
}

function isLocalHostname(hostname) {
  if (typeof hostname !== "string") {
    return false;
  }

  const normalized = hostname.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  if (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "[::1]"
  ) {
    return true;
  }

  if (
    /^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(normalized) ||
    /^192\.168\.\d{1,3}\.\d{1,3}$/.test(normalized) ||
    /^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(normalized)
  ) {
    return true;
  }

  return normalized.endsWith(".local");
}

export function getDefaultGraphQLEndpoint(hostname = window.location.hostname) {
  return isLocalHostname(hostname)
    ? LOCAL_GRAPHQL_ENDPOINT
    : PROD_GRAPHQL_ENDPOINT;
}

export function resolveGraphQLEndpoint(options = {}) {
  const search =
    typeof options.search === "string" ? options.search : window.location.search;
  const hostname =
    typeof options.hostname === "string"
      ? options.hostname
      : window.location.hostname;
  const queryParams = new URLSearchParams(search);

  const queryOverride = parseOverrideValue(
    queryParams.get(GRAPHQL_ENDPOINT_QUERY_PARAM)
  );
  if (queryOverride.action === "reset") {
    safeRemoveStorage(GRAPHQL_ENDPOINT_STORAGE_KEY);
  } else if (queryOverride.action === "set") {
    safeWriteStorage(GRAPHQL_ENDPOINT_STORAGE_KEY, queryOverride.endpoint);
    return queryOverride.endpoint;
  }

  const runtimeOverride = parseOverrideValue(window.GRAPHQL_ENDPOINT);
  if (runtimeOverride.action === "set") {
    return runtimeOverride.endpoint;
  }

  const storageOverride = parseOverrideValue(
    safeReadStorage(GRAPHQL_ENDPOINT_STORAGE_KEY)
  );
  if (storageOverride.action === "set") {
    return storageOverride.endpoint;
  }

  const envOverride = parseOverrideValue(import.meta.env.VITE_GRAPHQL_ENDPOINT);
  if (envOverride.action === "set") {
    return envOverride.endpoint;
  }

  return getDefaultGraphQLEndpoint(hostname);
}
